/**
 * What the generator will actually accept from an upload.
 *
 * The two file inputs already carry an `accept` attribute, and it is correct.
 * But `accept` only filters the picker's default view — choosing "All Files"
 * in the OS dialog defeats it, and it does nothing for a file handed between
 * pages in memory, which is how the homepage composer passes one to /generate.
 *
 * On 2026-09-03 a .zip got through that gap. The generator rejected it, exactly
 * as designed, with a clear message. The portal showed "Generation failed
 * (400)." instead, and the person retried the same file twelve seconds later.
 * Those were two of the eight generation jobs this product has ever run.
 *
 * So the check lives here too, in front of the request. Same list the API
 * enforces (api/routes/skills.py, generate_from_upload) — if that list changes,
 * change this one in the same commit.
 */
export const ACCEPT = '.pdf,.html,.htm,.jpg,.jpeg,.png,.gif,.webp,.zip';

const EXTENSIONS = ['.pdf', '.html', '.htm', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.zip'];

const MIME_PREFIXES = ['image/'];
// Browsers and operating systems disagree about what a .zip is called, and
// plenty of clients give up and send application/octet-stream — which is why
// the extension check above carries most of the weight for archives.
const MIME_EXACT = [
  'application/pdf', 'text/html', 'application/xhtml+xml',
  'application/zip', 'application/x-zip-compressed', 'application/x-zip',
  'multipart/x-zip',
];

/** Human wording for what is allowed, used in both the hint and the error. */
export const SUPPORTED_LABEL =
  'PDF, HTML, an image (JPEG, PNG, GIF, WebP), or a .zip of documents';

/**
 * @returns {string|null} null when the file is fine, otherwise the reason to
 * show. Phrased the way the generator phrases it, so a person who hits the
 * server-side check and the client-side one reads the same sentence twice
 * rather than two different explanations of one rule.
 */
export function rejectReason(file) {
  if (!file) return null;
  const name = (file.name || '').toLowerCase();
  const type = (file.type || '').toLowerCase();

  const extOk = EXTENSIONS.some((e) => name.endsWith(e));
  const mimeOk =
    MIME_EXACT.includes(type) || MIME_PREFIXES.some((p) => type.startsWith(p));

  // Either signal is enough. Browsers report an empty or generic type for some
  // files (and Windows sometimes sends application/octet-stream for a PDF), so
  // requiring both would reject files the API would happily have taken.
  if (extOk || mimeOk) return null;

  const shown = name.includes('.') ? name.slice(name.lastIndexOf('.')) : type || 'that file';
  return `Can't read ${shown} files. Supported: ${SUPPORTED_LABEL}.`;
}
